import React from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import RobotAuthentication from '../../models/robots/robot-authentication';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import { resetRobotsRestState } from '../../store/robots/robots-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import {
  validateRobotServiceAdminPassword,
  validateRobotUserPassword,
} from '../../services/validation/robot-validation';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import RobotAuthenticationPasswordInput from './RobotAuthenticationPasswordInput';
import { patchRobotAuthenticationSettingsById } from '../../store/robots/robots-actions';
import { selectIsUserAdminOrHigher, selectUser } from '../../store/general/general-slice-selectors';
import OrganizationRole from '../../models/enums/organization-roles';
import Button from '@mui/material/Button';

const AuthenticationSettings: React.FC<{
  robotId: string;
  authenticationSettings: RobotAuthentication;
}> = ({ robotId, authenticationSettings }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const user = useAppSelector((state) => selectUser(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: robotsData.rest.patchAuthenticationSettingsError,
      resetPatchHandler: () =>
        dispatch(
          resetRobotsRestState({
            patchAuthenticationSettingsStatus: HttpFetchStatus.Idle,
            patchAuthenticationSettingsError: null,
          }),
        ),
    });

  const {
    value: userPassword,
    validationResult: userPasswordValidationResult,
    valueChangeHandler: userPasswordChangeHandler,
  } = useValidatedInputEffect(
    authenticationSettings?.userPassword ?? '',
    (newValue) => validateRobotUserPassword(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('userPassword'),
    removeServerError,
    resetToggle,
  );

  const {
    value: serviceAdminPassword,
    validationResult: serviceAdminPasswordValidationResult,
    valueChangeHandler: serviceAdminPasswordChangeHandler,
  } = useValidatedInputEffect(
    authenticationSettings?.serviceAdminPassword ?? '',
    (newValue) => validateRobotServiceAdminPassword(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('serviceAdminPassword'),
    removeServerError,
    resetToggle,
  );

  const patchAuthenticationSettingsHandler = () => {
    dispatch(
      patchRobotAuthenticationSettingsById({
        id: robotId,
        data: {
          userPassword:
            userPassword !== authenticationSettings.userPassword ? userPassword : undefined,
          serviceAdminPassword:
            authenticationSettings.serviceAdminPassword != null ? serviceAdminPassword : undefined,
        },
      }),
    );
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchAuthenticationSettingsHandler();
  };

  return (
    <form onSubmit={formSubmitHandler}>
      <Stack spacing={4}>
        {robotsData.rest.patchAuthenticationSettingsError !== null &&
          isHttpError(robotsData.rest.patchAuthenticationSettingsError) && (
            <CustomAlert
              severity="error"
              header={t(robotsData.rest.patchAuthenticationSettingsError.message)}
            />
          )}

        <RobotAuthenticationPasswordInput
          value={userPassword}
          onBlur={patchAuthenticationSettingsHandler}
          onChange={userPasswordChangeHandler}
          validationResult={userPasswordValidationResult}
          timestamp={authenticationSettings.userPasswordTimestamp}
          label={t('input-label-robot-user-password')}
        />
        {![OrganizationRole.LOCATION, OrganizationRole.CUSTOMER].includes(
          user.data.organization.role,
        ) && (
          <RobotAuthenticationPasswordInput
            onBlur={patchAuthenticationSettingsHandler}
            value={serviceAdminPassword}
            onChange={serviceAdminPasswordChangeHandler}
            validationResult={serviceAdminPasswordValidationResult}
            timestamp={authenticationSettings.serviceAdminPasswordTimestamp}
            label={t('input-label-robot-service-admin-password')}
          />
        )}
        {isAdmin && (
          <Button
            sx={{ display: 'none' }}
            disabled={robotsData.rest.patchAuthenticationSettingsStatus === HttpFetchStatus.Loading}
            type="submit"
          />
        )}
      </Stack>
    </form>
  );
};

export default AuthenticationSettings;
